import { XClose } from "@untitledui/icons";
import type { ComponentType, ReactNode } from "react";
import { useEffect, useState } from "react";
import { Dialog as AriaDialog, Modal as AriaModal, ModalOverlay as AriaModalOverlay } from "react-aria-components";
import type { FieldValues, Path, SubmitHandler, UseFormReturn } from "react-hook-form";
import { FormProvider } from "react-hook-form";
import { IconTopNumber } from "@/components/base/application/progress-steps/step-base";
import { Button } from "@/components/base/buttons/button";
import { PortalContainer } from "@/components/ui/portal-container";
import { collectErrorMessages, pickErrorMessages } from "./step-errors";

export type StepperStep<T extends FieldValues> = {
  id: string;
  label: string;
  render: () => ReactNode;
  fields?: readonly Path<T>[];
};

type Props<T extends FieldValues> = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  subtitle: string;
  icon: ComponentType<{ className?: string }>;
  form: UseFormReturn<T>;
  steps: StepperStep<T>[];
  onSubmit: SubmitHandler<T>;
  submitLabel: string;
  submitPendingLabel: string;
  isSubmitting?: boolean;
  allowStepJump?: boolean;
  submitFromAnyStep?: boolean;
  initialStepId?: string;
};

export function CollabStepperModal<T extends FieldValues>({
  open,
  onOpenChange,
  title,
  subtitle,
  icon: Icon,
  form,
  steps,
  onSubmit,
  submitLabel,
  submitPendingLabel,
  isSubmitting = false,
  allowStepJump = false,
  submitFromAnyStep = false,
  initialStepId,
}: Props<T>) {
  useEffect(() => {
    if (!open) return;
    const previous = document.documentElement.style.getPropertyValue("--sidebar-top-offset");
    document.documentElement.style.setProperty("--sidebar-top-offset", "0px");
    return () => {
      if (previous) {
        document.documentElement.style.setProperty("--sidebar-top-offset", previous);
      } else {
        document.documentElement.style.removeProperty("--sidebar-top-offset");
      }
    };
  }, [open]);

  const [currentStep, setCurrentStep] = useState(0);
  const [footerErrors, setFooterErrors] = useState<string[]>([]);
  const [dialogNode, setDialogNode] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setCurrentStep(
      Math.max(
        0,
        steps.findIndex((s) => s.id === initialStepId),
      ),
    );
    setFooterErrors([]);
  }, [open]);

  const isLastStep = currentStep === steps.length - 1;
  const step = steps[currentStep];

  const handleClose = () => {
    onOpenChange(false);
    form.reset();
    setCurrentStep(0);
    setFooterErrors([]);
  };

  const submitNow = async () => {
    setFooterErrors([]);
    await form.handleSubmit(onSubmit, (errors) => {
      setFooterErrors(collectErrorMessages(errors));
      const errored = new Set(Object.keys(errors));
      const firstStepWithError = steps.findIndex((s) => s.fields?.some((field) => errored.has(field as string)));
      if (firstStepWithError >= 0) setCurrentStep(firstStepWithError);
    })();
  };

  const handleContinue = async () => {
    const fields = step?.fields;
    if (fields && fields.length > 0) {
      const valid = await form.trigger(fields as Path<T>[]);
      setFooterErrors(valid ? [] : pickErrorMessages(form.formState.errors, fields));
      if (!valid) return;
    }

    if (isLastStep) {
      await submitNow();
      return;
    }

    setFooterErrors([]);
    setCurrentStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const goToStep = (index: number) => {
    setFooterErrors([]);
    setCurrentStep(index);
  };

  return (
    <AriaModalOverlay
      isOpen={open}
      onOpenChange={handleClose}
      isDismissable={false}
      className="fixed inset-0 z-[120] bg-black/70"
    >
      <AriaModal className="absolute inset-0 flex flex-col outline-none" style={{ background: "#fffcf9" }}>
        <AriaDialog className="w-full h-full flex flex-col outline-none" ref={setDialogNode}>
          <div className="flex items-center justify-between px-8 py-5 border-b border-secondary shrink-0">
            <div className="flex items-center gap-3">
              <Icon className="size-5 text-secondary" />
              <div>
                <h2 className="text-lg font-semibold text-primary">{title}</h2>
                <p className="text-sm text-tertiary">{subtitle}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="flex items-center justify-center rounded-lg p-1.5 hover:bg-black/5 transition-colors"
            >
              <XClose className="size-5 text-secondary" />
            </button>
          </div>

          <div style={{ background: "#fcf6f1" }} className="px-8 py-6 shrink-0">
            <div
              style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}
              className="grid w-full items-start gap-4"
            >
              {steps.map((item, i) => (
                <StepMarker
                  key={item.id}
                  label={item.label}
                  step={i + 1}
                  status={i < currentStep ? "complete" : i === currentStep ? "current" : "incomplete"}
                  connector={i !== steps.length - 1}
                  onClick={allowStepJump ? () => goToStep(i) : undefined}
                />
              ))}
            </div>
          </div>

          <PortalContainer.Provider value={dialogNode}>
            <FormProvider {...form}>
              <div className="flex-1 overflow-auto px-8 py-8 flex justify-center">
                <div className="w-full max-w-[716px]">{step?.render()}</div>
              </div>
            </FormProvider>
          </PortalContainer.Provider>

          <div className="flex items-center justify-between px-8 py-5 border-t border-secondary shrink-0">
            <Button
              color="secondary"
              size="lg"
              type="button"
              isDisabled={currentStep === 0}
              onClick={() => goToStep(currentStep - 1)}
            >
              Previous
            </Button>
            <div className="flex items-center gap-3">
              {footerErrors.length > 0 && (
                <div className="flex flex-col items-end gap-0.5">
                  {footerErrors.map((message) => (
                    <p key={message} className="text-sm text-error-primary">
                      {message}
                    </p>
                  ))}
                </div>
              )}
              {submitFromAnyStep && !isLastStep ? (
                <>
                  <Button color="secondary" size="lg" type="button" onClick={handleContinue}>
                    Continue
                  </Button>
                  <Button color="primary" size="lg" type="button" isDisabled={isSubmitting} onClick={submitNow}>
                    {isSubmitting ? submitPendingLabel : submitLabel}
                  </Button>
                </>
              ) : (
                <Button color="primary" size="lg" type="button" isDisabled={isSubmitting} onClick={handleContinue}>
                  {isLastStep ? (isSubmitting ? submitPendingLabel : submitLabel) : "Continue"}
                </Button>
              )}
            </div>
          </div>
        </AriaDialog>
      </AriaModal>
    </AriaModalOverlay>
  );
}

function StepMarker({
  label,
  step,
  status,
  connector,
  onClick,
}: {
  label: string;
  step: number;
  status: "complete" | "current" | "incomplete";
  connector: boolean;
  onClick?: () => void;
}) {
  const marker = (
    <IconTopNumber title={label} description="" status={status} step={step} size="sm" connector={connector} />
  );

  return onClick ? (
    <button type="button" onClick={onClick} className="w-full text-left">
      {marker}
    </button>
  ) : (
    <div className="w-full">{marker}</div>
  );
}
